import { AccountModel } from 'data-ng-authentication';
import * as _4 from 'lodash4';
import { StrategySafe } from 'utility-ts-strategy';
import { ICommentsDatasource } from '../../../../comments/datasources/comments_datasource';
import { CommentServant } from '../../../../comments/servants/comment_servant';
import { APGenericSlot, CourseGroupSlot, CourseSlot } from '../../../../global/builders/ap_generic_slot_builder';
import { IAPAPMarkAsMetStatusDatasource } from '../../../../global/components/mark_as_met/datasources/mark_as_met_status_datasource';
import { APGenericSlotServant } from '../../../../global/servants/ap_generic_slot_servant';
import { APMinGradeServant } from '../../../../global/servants/ap_min_grade_servant';
import { ap_course_status_type } from '../../../../global/strategies/ap_course_status_strategy';

export const SlotStatusCssClassColorMap = new StrategySafe<string, string>({
  GREEN: 'green',
  GRAY: 'gray',
  PURPLE: 'purple',
  YELLOW: 'warning'
});

type course_status_with_null = ap_course_status_type;
export const SlotStatusTextToColorMap = new StrategySafe<string, course_status_with_null>({
  PLANNED: 'GREEN',
  WAITLISTED: 'PURPLE',
  SECTION_PICKED: 'GREEN',
  IN_PROGRESS: 'GREEN',
  COMPLETED: 'GRAY',
  MET: 'GRAY',
  DEFAULT: 'GREEN',
  REGISTERED: 'GREEN',
  '': 'GREEN'
});

export const SlotStatusTextMap = new StrategySafe<string, course_status_with_null | 'PLACEHOLDER'>({
  PLANNED: 'Planned',
  WAITLISTED: 'Waitlisted',
  SECTION_PICKED: 'Planned',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  MET: 'Met',
  DEFAULT: 'Planned',
  REGISTERED: 'Enrolled',
  '': 'Planned',
  PLACEHOLDER: 'Placeholder'
});

export class APSlotStatusBadgeHtmlServant {
  constructor(
    private ngCommentsDatasource: ICommentsDatasource,
    private accountModel: AccountModel,
    private ngAPMarkAsMetStatusDatasource: IAPAPMarkAsMetStatusDatasource,
    public appConfigModel: any
  ) {}

  getMarkAsMetMessage(slotId: APGenericSlot['programSlot']['id']): string {
    const comment = _4.first(this.ngCommentsDatasource.query((cmnt) => cmnt.object_id === slotId));
    const defaultMessage = `marked this requirement as met`;
    if (!comment) {
      return _4.upperFirst(defaultMessage);
    }
    const sender = comment.sender_info;
    const whoMarked = this.accountModel.id === sender.id ? 'You' : `${sender.first_name} ${sender.last_name}`;
    if (!comment || _4.isEmpty(comment.message)) {
      return `${whoMarked} ${defaultMessage}`;
    }
    return `<div class="pbl"> ${whoMarked} ${defaultMessage} </div> "${comment.message}"`;
  }

  getMarkAsMetBadge(genericSlot: APGenericSlot): string {
    const slotId = genericSlot.programSlot.id;
    const statusEntry = _4.first(this.ngAPMarkAsMetStatusDatasource.query((slot) => slot.program_slot === slotId));
    const showBadgeWithComments = statusEntry && statusEntry.status === 'MET';
    if (!showBadgeWithComments) {
      return '';
    }
    const uniqueId = `pgm-slot-met-${_4.uniqueId()}`;
    return `
            <hi-badge theme="${SlotStatusCssClassColorMap['GRAY']}">
                <div l-kind l-width-fixed l-align="center">
                    <hi-icon class="mrsx" kind="info_circle" theme="gray-dark" id=${uniqueId} type="material"></hi-icon>
                    Met
                </div>
            </hi-badge>
            <hi-tooltip ng-attr-for=${uniqueId}>
                ${this.getMarkAsMetMessage(slotId)}
            </hi-tooltip>
        `;
  }

  isCourseRegistered(genericSlot: APGenericSlot) {
    return _4.includes(['REGISTERED'], _4.get(genericSlot, 'plannedCourse.status', ''));
  }

  shouldShowNoBadge(genericSlot: APGenericSlot, isInLeftPanel: boolean) {
    if (!APGenericSlotServant.isNCRSlot(genericSlot) && APGenericSlotServant.isGenericSlotCoursePlanned(genericSlot)) {
      if (this.isCourseRegistered(genericSlot) || APGenericSlotServant.isCourseGroupSlot(genericSlot)) {
        return false;
      }
      if (APMinGradeServant.progSlotEmptyPlannedCourseCompleted(genericSlot.programSlot, genericSlot.plannedCourse)) {
        return true;
      }
      return isInLeftPanel ? !genericSlot.isMinGradeMet && genericSlot.isCourseCompleted : true;
    }
    return false;
  }

  getPlannedStatusBadge(genericSlot: APGenericSlot) {
    const isInLeftPanel = APGenericSlotServant.isGenericSlotInDegreeMap(genericSlot);
    const isPlanned = APGenericSlotServant.isGenericSlotPlanned(genericSlot);
    const noBadge = '';
    if (!isPlanned || this.shouldShowNoBadge(genericSlot, isInLeftPanel)) {
      return noBadge;
    }
    let statusText = '';
    let cssClass = '';
    if (APGenericSlotServant.isNCRSlot(genericSlot)) {
      statusText = SlotStatusTextMap['PLANNED'];
      cssClass = SlotStatusCssClassColorMap['GREEN'];
    } else if (
      isInLeftPanel &&
      (APGenericSlotServant.isCourseSlot(genericSlot) || APGenericSlotServant.isGenericSlotCoursePlanned(genericSlot))
    ) {
      statusText = SlotStatusTextMap[genericSlot.plannedCourse.status];
      cssClass = SlotStatusCssClassColorMap[SlotStatusTextToColorMap[genericSlot.plannedCourse.status]];
    } else {
      if (isInLeftPanel) {
        statusText = SlotStatusTextMap['PLANNED'];
        cssClass = SlotStatusCssClassColorMap['GREEN'];
      } else {
        statusText = SlotStatusTextMap['PLACEHOLDER'];
        cssClass = SlotStatusCssClassColorMap['GRAY'];
      }
    }
    return `<hi-badge theme="${cssClass}"> ${statusText} </hi-badge>`;
  }

  getReqBadge(genericSlot: APGenericSlot) {
    const emptyReqBadge = '';
    if (!APGenericSlotServant.isCourseSlot(genericSlot)) {
      return emptyReqBadge;
    }
    const course = genericSlot.course;
    const noReqFound = !course.has_coreq && !course.has_prereq;
    if (noReqFound) {
      return emptyReqBadge;
    }
    return `
            <hi-badge theme="${SlotStatusCssClassColorMap['YELLOW']}">
                <div l-kind l-width-fixed l-align="center">
                  <hi-icon size="small" kind="link" class="mrsx" theme="${SlotStatusCssClassColorMap['YELLOW']}"></hi-icon>
                  Requisite
                </div>
            </hi-badge>
        `;
  }

  getGradeBadge(genericSlot: APGenericSlot) {
    const emptyMinGradeBadge = '';
    if (!(APGenericSlotServant.isCourseSlot(genericSlot) || APGenericSlotServant.isCourseGroupSlot(genericSlot))) {
      return emptyMinGradeBadge;
    }
    const showGradeBadge = genericSlot.showGradeBadge;
    if (!showGradeBadge || !genericSlot.programSlot._grade_config) {
      return emptyMinGradeBadge;
    }

    const badgeId = `status-badge-${genericSlot.programSlot.id}-${_4.uniqueId()}`;
    return `
      <hi-badge id="${badgeId}" theme="${SlotStatusCssClassColorMap['BLUE']}">
        <span>
          Min Grade: ${genericSlot.programSlot.min_grade_point}
        </span>
      </hi-badge>
      <hi-tooltip placement="top" for="${badgeId}">
      <h4>Minimum Grade</h4>
        ${genericSlot.programSlot._grade_config.min_grade_text}
      <h4>Grade Explanation</h4>
        ${genericSlot.programSlot._grade_config.grade_explanation}
      </hi-tooltip>`;
  }

  getBadges(genericSlot: APGenericSlot): string[] {
    const badges: string[] = [];
    // order of badges: req badges, status badges, mark as met badges, Min Grade badge
    badges.push(this.getReqBadge(genericSlot));
    badges.push(this.getPlannedStatusBadge(genericSlot));
    if (APGenericSlotServant.isGenericSlotInDegreeMap(genericSlot)) {
      badges.push(this.getMarkAsMetBadge(genericSlot));
    }
    badges.push(this.getGradeBadge(genericSlot));
    return _4.compact(badges);
  }
}
angular
  .module('capability-ng-academic-planner')
  .service('ngAPSlotStatusBadgeHtmlServant', APSlotStatusBadgeHtmlServant);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/plan/components/slot_status_badge/servants/slot_status_badge_html_servant.ts